// Week grid (nursing-ops-2 #07): nurses × days for one week of the ward
// roster. A VIEWING surface with light editing — clicking a cell opens a
// popover to set/change/remove that single assignment; every mutation is one
// call on the existing row-level endpoints (addWardRoster /
// updateWardRosterStatus / removeWardRoster), so overtime-cap and (later)
// leave-block rejections surface per cell exactly as the backend words them.
// Mass operations stay in the batch authoring form, per the ratified decision.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, IconButton, MenuItem, Paper, Popover, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography, LinearProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import { NURSE_COLORS, initials, nurseAvatarColor } from "../theme";
import { addWardRoster, getWardRoster, updateWardRosterStatus, removeWardRoster, listOf } from "../api/nurseApi";
import { shiftLabel } from "../shiftWindow";
import { useShiftSchedule } from "../hooks";

const STATUS_OPTIONS = [
  { value: "on_duty", label: "On Duty" },
  { value: "overtime", label: "Overtime" },
  { value: "leave", label: "Leave" },
  { value: "na", label: "N/A" },
];

// Day-cell background by shift POSITION, not by name (2026-08-17): the
// facility defines its own shifts, so codes this module has never seen are
// normal. The palette is indexed by the shift's place in the schedule and
// cycles, which also keeps colours stable as long as the order is.
const SHIFT_PALETTE = [
  { bg: "#FEF3C7", ink: "#92400E" },
  { bg: "#FFEDD5", ink: "#9A3412" },
  { bg: "#E0E7FF", ink: "#3730A3" },
  { bg: "#DCFCE7", ink: "#166534" },
  { bg: "#FCE7F3", ink: "#9D174D" },
  { bg: "#E0F2FE", ink: "#075985" },
];
const blockFor = (schedule, code) => {
  const idx = schedule.findIndex((s) => s.code === code);
  return SHIFT_PALETTE[(idx < 0 ? 0 : idx) % SHIFT_PALETTE.length];
};

const toStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Monday of the week containing `date`.
function mondayOf(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function WeekGrid({
  wards = [], nurses = [], onChanged,
  onBulkAssign, onCopyWeek, copying = false,
}) {
  const { schedule } = useShiftSchedule();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Ward context for creating from an empty cell (grid shows ALL wards' rows;
  // creation needs an explicit target ward, same cascade as the form).
  const [wardCode, setWardCode] = useState("");
  // Popover state: { anchorEl, nurse, date }
  const [cell, setCell] = useState(null);
  const [newShift, setNewShift] = useState("");
  const [newStatus, setNewStatus] = useState("on_duty");
  const [saving, setSaving] = useState(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return toStr(d);
    }),
    [weekStart]
  );

  const reload = useCallback(() => {
    setLoading(true);
    getWardRoster({ from_date: days[0], to_date: days[6] })
      .then((r) => setRows(listOf(r)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { reload(); }, [reload]);

  const byNurseDate = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const key = `${r.nurse_id}|${r.roster_date}`;
      (m[key] = m[key] || []).push(r);
    });
    return m;
  }, [rows]);

  // Resolve the ward code stored on each roster row back to a readable name for
  // the day-cell blocks. A ward is a room category — there is no department half
  // to resolve any more.
  const wardNameByCode = useMemo(() => {
    const m = {};
    wards.forEach((w) => { if (!(w.code in m)) m[w.code] = w.name; });
    return m;
  }, [wards]);

  const closeCell = () => { setCell(null); setNewShift(schedule[0]?.code || ""); setNewStatus("on_duty"); };

  const afterMutation = () => { reload(); onChanged?.(); };

  const handleAdd = async () => {
    if (!cell || !wardCode) {
      toast.warn("Pick the ward (top of the grid) to add an assignment.");
      return;
    }
    setSaving(true);
    try {
      const res = await addWardRoster({
        nurse_id: cell.nurse.nurse_id,
        nurse_name: cell.nurse.name,
        ward_code: wardCode,
        shift: newShift,
        status: newStatus,
        roster_date: cell.date,
      });
      const errors = res?.errors || [];
      if (res?.status !== "Success" || errors.length > 0) {
        toast.error(errors[0]?.error || res?.message || "Could not add assignment");
      } else if ((res?.skipped || []).length > 0) {
        toast.warn(res.skipped[0]?.reason || "Assignment already exists");
      } else {
        toast.success("Assignment added");
        afterMutation();
        closeCell();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add assignment");
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (row, status) => {
    if (status === row.status) return;
    setSaving(true);
    try {
      const res = await updateWardRosterStatus(row.id, status);
      if (res?.status !== "Success") toast.error(res?.message || "Status update failed");
      else afterMutation();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row) => {
    setSaving(true);
    try {
      const res = await removeWardRoster(row.id);
      if (res?.status !== "Success") toast.error(res?.message || "Remove failed");
      else { toast.success("Assignment removed"); afterMutation(); }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Remove failed");
    } finally {
      setSaving(false);
    }
  };

  const shiftWeek = (deltaDays) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(d);
  };

  // Seed the picker from the schedule once it arrives — "morning" may not be
  // a code this facility defines.
  useEffect(() => { setNewShift((cur) => cur || schedule[0]?.code || ""); }, [schedule]);

  const cellRows = cell ? byNurseDate[`${cell.nurse.nurse_id}|${cell.date}`] || [] : [];

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5, alignItems: { md: "center" }, justifyContent: "space-between" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, flexWrap: "wrap" }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <IconButton size="small" onClick={() => shiftWeek(-7)}><ChevronLeftIcon fontSize="small" /></IconButton>
            <Typography variant="subtitle2" sx={{ minWidth: 170, textAlign: "center" }}>
              {days[0]} → {days[6]}
            </Typography>
            <IconButton size="small" onClick={() => shiftWeek(7)}><ChevronRightIcon fontSize="small" /></IconButton>
          </Stack>
          <TextField
            select size="small" label="Ward (for new cells)" value={wardCode}
            onChange={(e) => setWardCode(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {wards.map((w) => (<MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1}>
          {onCopyWeek && (
            <Tooltip title="Duplicate this week's roster into next week (skips duplicates; each row re-checked against the overtime cap and approved leave)">
              <span>
                <Button size="small" variant="outlined" disabled={copying} onClick={onCopyWeek}>
                  {copying ? "Copying..." : "Copy this week → next week"}
                </Button>
              </span>
            </Tooltip>
          )}
          {onBulkAssign && (
            <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />} onClick={onBulkAssign}>
              Bulk assign shifts
            </Button>
          )}
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1300 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell sx={{ minWidth: 160 }}>Nurse</TableCell>
              {days.map((d) => (
                <TableCell key={d} sx={{ minWidth: 160 }}>
                  {d.slice(5)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {nurses.map((n) => (
              <TableRow key={n.nurse_id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: nurseAvatarColor(n.nurse_id), width: 28, height: 28, fontSize: 12 }}>
                      {initials(n.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{n.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{n.specialization || n.department || "—"}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                {days.map((d) => {
                  const assignments = byNurseDate[`${n.nurse_id}|${d}`] || [];
                  return (
                    <TableCell
                      key={d}
                      onClick={(e) => setCell({ anchorEl: e.currentTarget, nurse: n, date: d })}
                      sx={{ cursor: "pointer", verticalAlign: "top", "&:hover": { bgcolor: NURSE_COLORS.pageBg } }}
                    >
                      {assignments.length > 0 ? (
                        <Stack direction="column" spacing={0.75} sx={{ alignItems: "stretch" }}>
                          {assignments.map((a) => {
                            const block = blockFor(schedule, a.shift);
                            const isOvertime = a.status === "overtime";
                            const isOff = a.status === "leave" || a.status === "na";
                            const location = wardNameByCode[a.ward_code] || a.ward_code || "—";
                            return (
                              <Tooltip key={a.id} title={`${a.shift} — ${a.status} · ${location}`}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    borderRadius: 1.5,
                                    px: 1.25, py: 0.75,
                                    width: "100%",
                                    boxSizing: "border-box",
                                    bgcolor: isOff ? "transparent" : block.bg,
                                    border: isOff ? `1px dashed ${NURSE_COLORS.border}` : "none",
                                  }}
                                >
                                  {isOvertime && (
                                    <Box
                                      sx={{
                                        position: "absolute", top: -4, right: -4, width: 10, height: 10,
                                        borderRadius: "50%", bgcolor: NURSE_COLORS.warning, border: "1.5px solid #fff",
                                      }}
                                    />
                                  )}
                                  <Typography
                                    sx={{ fontWeight: 700, fontSize: "0.8rem", lineHeight: 1.3, color: isOff ? "text.secondary" : block.ink }}
                                  >
                                    {isOff ? (a.status === "leave" ? "Leave" : "N/A") : shiftLabel(a.shift, schedule)}
                                  </Typography>
                                  <Typography
                                    noWrap
                                    title={location}
                                    sx={{
                                      fontSize: "0.7rem", lineHeight: 1.35, opacity: 0.9,
                                      color: isOff ? "text.disabled" : block.ink,
                                      overflow: "hidden", textOverflow: "ellipsis",
                                    }}
                                  >
                                    {location}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            border: `1px dashed ${NURSE_COLORS.border}`, borderRadius: 1.5, px: 1.25, py: 0.75,
                            width: "100%", boxSizing: "border-box",
                            color: "text.disabled", fontSize: "0.75rem",
                          }}
                        >
                          + set shift
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {nurses.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No nurses.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Popover
        open={Boolean(cell)}
        anchorEl={cell?.anchorEl}
        onClose={closeCell}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {cell && (
          <Box sx={{ p: 2, minWidth: 280 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {cell.nurse.name} — {cell.date}
            </Typography>
            {cellRows.map((a) => (
              <Stack key={a.id} direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                <Chip size="small" label={a.shift} sx={{ minWidth: 72 }} />
                <TextField
                  select size="small" value={a.status} disabled={saving}
                  onChange={(e) => handleStatus(a, e.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  {STATUS_OPTIONS.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
                </TextField>
                <Tooltip title="Remove assignment">
                  <span>
                    <IconButton size="small" color="error" disabled={saving} onClick={() => handleRemove(a)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: cellRows.length ? 1.5 : 0 }}>
              <TextField select size="small" label="Shift" value={newShift} onChange={(e) => setNewShift(e.target.value)} sx={{ minWidth: 110 }}>
                {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
              </TextField>
              <TextField select size="small" label="Status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} sx={{ minWidth: 120 }}>
                {STATUS_OPTIONS.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
              </TextField>
              <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={saving} onClick={handleAdd}>
                Add
              </Button>
            </Stack>
            {!wardCode && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Adding uses the Ward selected above the grid.
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
